import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import axiosInstance from '../api/axiosConfig';
import {
  Avatar, Button, TextField, Box, Typography, Container, Alert, CircularProgress, Link,
  Menu, MenuItem, InputAdornment
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import FlagIcon from '../components/ui/FlagIcon';

const SetupAccount = () => {
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phonePrefix, setPhonePrefix] = useState('+32');
  const [localPhone, setLocalPhone] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const verifyToken = async () => {
      const tokenFromUrl = searchParams.get('token');
      if (tokenFromUrl) {
        try {
          const response = await axiosInstance.post('/auth/verify-setup-token', { token: tokenFromUrl });
          setToken(tokenFromUrl);
          setEmail(response.data.email);
          setIsTokenValid(true);
        } catch (err) {
          setError("Le lien d'invitation est invalide ou a expiré.");
          setIsTokenValid(false);
        }
      } else {
        setError("Token d'invitation manquant ou invalide.");
        setIsTokenValid(false);
      }
      setIsLoading(false);
    };
    verifyToken();
  }, [searchParams]);

  const handleFlagMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFlagMenuClose = () => {
    setAnchorEl(null);
  };

  const handlePrefixSelect = (prefix) => {
    setPhonePrefix(prefix);
    handleFlagMenuClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!firstName || !lastName || !password || !confirmPassword || !localPhone || !firstName.trim() || !lastName.trim() || !localPhone.trim()) {
      setError('Veuillez remplir tous les champs.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }

    const cleanedLocalPhone = localPhone.replace(/\s/g, '');
    let fullPhone = cleanedLocalPhone;

    if (!cleanedLocalPhone.startsWith(phonePrefix)) {
      // Si le numéro local commence par '0' et que c'est un préfixe belge ou français, on retire le '0'
      if (cleanedLocalPhone.startsWith('0') && (phonePrefix === '+32' || phonePrefix === '+33')) {
        fullPhone = phonePrefix + cleanedLocalPhone.substring(1);
      } else {
        fullPhone = phonePrefix + cleanedLocalPhone;
      }
    }

    try {
      await axiosInstance.post('/auth/setup-account', {
        token,
        firstName,
        lastName,
        phone: fullPhone,
        password,
      });
      setSuccess('Votre compte a été créé avec succès ! Vous allez être redirigé vers la page de connexion.');
      setTimeout(() => {
        navigate('/login');
      }, 4000);
    } catch (err) {
      const errorCode = err.response?.data?.error;
      switch (errorCode) {
        case 'INVALID_TOKEN':
          setError("Le lien d'invitation est invalide ou a expiré.");
          break;
        case 'WEAK_PASSWORD':
          setError('Le mot de passe est trop faible.');
          break;
        default:
          setError('Une erreur est survenue. Veuillez réessayer.');
      }
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
          <PersonAddIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Finaliser votre inscription
        </Typography>
        {isLoading ? (
          <CircularProgress sx={{ mt: 4 }} />
        ) : (
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3, width: '100%' }}>
            {error && <Alert severity="error" sx={{ width: '100%', mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ width: '100%', mb: 2 }}>{success}</Alert>}

            {!success && (
              <>
                <TextField
                  label="Prénom"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  fullWidth
                  margin="normal"
                  required
                  autoFocus
                  disabled={!isTokenValid}
                />
                <TextField
                  label="Nom"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  fullWidth
                  margin="normal"
                  required
                  disabled={!isTokenValid}
                />
                <TextField
                  label="Numéro de téléphone"
                  value={localPhone}
                  onChange={(e) => setLocalPhone(e.target.value)}
                  fullWidth
                  required
                  margin="normal"
                  disabled={!isTokenValid}
                  helperText={
                    phonePrefix === '+32' ? 'Ex: 0470123456' :
                    'Ex: 0612345678'
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Button onClick={handleFlagMenuOpen} sx={{ minWidth: 'auto', padding: '6px' }} disabled={!isTokenValid}>
                          <FlagIcon countryCode={phonePrefix === '+32' ? 'BE' : 'FR'} />
                        </Button>
                      </InputAdornment>
                    ),
                  }}
                />
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleFlagMenuClose}>
                  <MenuItem onClick={() => handlePrefixSelect('+32')}>
                    <FlagIcon countryCode="BE" sx={{ mr: 1.5 }} /> Belgique (+32)
                  </MenuItem>
                  <MenuItem onClick={() => handlePrefixSelect('+33')}>
                    <FlagIcon countryCode="FR" sx={{ mr: 1.5 }} /> France (+33)
                  </MenuItem>
                </Menu>
                <TextField
                  label="Adresse Email"
                  type="email"
                  value={email}
                  fullWidth
                  margin="normal"
                  disabled // Toujours désactivé
                />
                <TextField
                  label="Nouveau mot de passe"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  fullWidth
                  margin="normal"
                  required
                  disabled={!isTokenValid}
                />
                <TextField
                  label="Confirmer le mot de passe"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  fullWidth
                  margin="normal"
                  required
                  disabled={!isTokenValid}
                />
                <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }} disabled={!isTokenValid}>
                  Créer mon compte
                </Button>
              </>
            )}
          </Box>
        )}
        {!isLoading && !isTokenValid && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2">
              Veuillez contacter votre administrateur pour recevoir une nouvelle invitation.
            </Typography>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default SetupAccount;
